import React, { useEffect, useState } from "react";
import "./App.css";
import Welcome from "./components/welcome/welcome";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import PlayerSigned from "./components/player-signed/player-sign";
import WelcomeForm from "./components/welcome/welcome-form";
import Dashboard from "./components/dashboard/dashboard";
import DeveloperDashboard from "./components/developer/developer-dashboard";
import DeveloperWelcome from "./components/developer/developer-welcome";
import DeveloperWelcomeForm from "./components/developer/developer-welcome-form";
import Settings from "./components/settings/Settings";
import ForgotPassword from "./components/auth/ForgotPassword";
import ResetPassword from "./components/auth/ResetPassword";
import CreateAccount from "./components/auth/CreateAccount";
import VerifyEmail from "./components/auth/VerifyEmail";
import Rewards from "./components/rewards/Rewards";
import UploadVideo from "./components/video/upload-video";
import TimelineScreen from "./components/timeline-screen/timeline-screen";
import Tencent from "./components/tencent/tencent";
import OverwolfAuthTest from "./components/welcome/OverwolfAuthTest";
import ValidationStatus from "./components/validation/ValidationStatus";
import AdminDashboard from "./components/admin/AdminDashboard";
import ImmerselyDemo from "./components/demo/ImmerselyDemo";
import { getCurrentUserProfile } from "./services/dashboardService";
import { isProfileComplete } from "./utils/profile";
import 'bootstrap/dist/css/bootstrap.min.css';

// Helper function to decode JWT token
function decodeJWT(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    // console.error("Error decoding JWT:", error);
    return null;
  }
}

// Protected Route Component
function ProtectedRoute({ children, allowedRoles = [], requireProfile = false }) {
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState(null);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const [redirectPath, setRedirectPath] = useState('/');
  const token = localStorage.getItem('token');
  const hasCheckedRole = React.useRef(false);

  useEffect(() => {
    // Prevent multiple checks
    if (hasCheckedRole.current) {
      return;
    }

    const checkUserRole = async () => {
      if (!token) {
        setLoading(false);
        setShouldRedirect(true);
        setRedirectPath('/');
        return;
      }

      try {
        // First, try to get role from JWT token
        const decodedToken = decodeJWT(token);
        let role = decodedToken?.role || decodedToken?.userRole;
        let email = decodedToken?.email || decodedToken?.userEmail;
        
        // Always try to get from profile API to get latest role
        const profile = await getCurrentUserProfile();
        
        if (profile) {
          email = profile.email || email;
          // Use role from API if available, otherwise use token role
          role = profile.role || role;
        }
        
        // Fallback to localStorage or default if still no role
        if (!role) {
          role = localStorage.getItem('userRole') || 'playtester';
        }
        
        setUserRole(role);
        
        if (requireProfile && profile && role !== "admin" && !isProfileComplete(profile)) {
          setShouldRedirect(true);
          setRedirectPath(
            role === "developer"
              ? "/developer/welcome?profile_completed=false"
              : "/welcome?profile_completed=false"
          );
        } else if (allowedRoles.length > 0 && !allowedRoles.includes(role)) {
          // Redirect to appropriate dashboard based on role
          setShouldRedirect(true);
          if (role === 'admin') {
            setRedirectPath('/admin');
          } else if (role === 'developer') {
            setRedirectPath('/developer');
          } else {
            setRedirectPath('/dashboard');
          }
        }
      } catch (error) {
        console.error("Error checking user role:", error);
        // Fallback to localStorage or default
        const fallbackRole = localStorage.getItem('userRole') || 'playtester';
        setUserRole(fallbackRole);
        
        // Check if fallback role is allowed
        if (allowedRoles.length > 0 && !allowedRoles.includes(fallbackRole)) {
          setShouldRedirect(true);
          if (fallbackRole === 'admin') {
            setRedirectPath('/admin');
          } else if (fallbackRole === 'developer') {
            setRedirectPath('/developer');
          } else {
            setRedirectPath('/dashboard');
          }
        }
      } finally {
        setLoading(false);
        hasCheckedRole.current = true;
      }
    };

    checkUserRole();
  }, []); // Empty dependency array - only run once

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        color: 'white'
      }}>
        Loading...
      </div>
    );
  }

  if (shouldRedirect) {
    return <Navigate to={redirectPath} replace />;
  }

  return children;
}

// Route Guard Component for dashboard redirect
function DashboardRedirect() {
  const [loading, setLoading] = useState(true);
  const [redirectPath, setRedirectPath] = useState('/dashboard');
  const hasCheckedRole = React.useRef(false);

  useEffect(() => {
    // Prevent multiple checks
    if (hasCheckedRole.current) {
      return;
    }

    const checkUserRole = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        setRedirectPath('/');
        hasCheckedRole.current = true;
        return;
      }

      try {
        // First, try to get role from JWT token
        const decodedToken = decodeJWT(token);
        let role = decodedToken?.role || decodedToken?.userRole;
        let email = decodedToken?.email || decodedToken?.userEmail;
        
        // Always try to get from profile API to get latest role
        const profile = await getCurrentUserProfile();
        
        if (profile) {
          email = profile.email || email;
          // Use role from API if available, otherwise use token role
          role = profile.role || role;
        }
        
        // Fallback to localStorage or default
        if (!role) {
          role = localStorage.getItem('userRole') || 'playtester';
        }
        
        // Set redirect path based on role
        if (role === 'admin') {
          setRedirectPath('/admin');
        } else if (role === 'developer') {
          setRedirectPath('/developer');
        } else {
          setRedirectPath('/dashboard');
        }
      } catch (error) {
        console.error("Error checking user role:", error);
        const fallbackRole = localStorage.getItem('userRole') || 'playtester';
        
        if (fallbackRole === 'admin') {
          setRedirectPath('/admin');
        } else if (fallbackRole === 'developer') {
          setRedirectPath('/developer');
        } else {
          setRedirectPath('/dashboard');
        }
      } finally {
        setLoading(false);
        hasCheckedRole.current = true;
      }
    };

    checkUserRole();
  }, []); // Empty dependency array - only run once

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        color: 'white'
      }}>
        Loading...
      </div>
    );
  }

  return <Navigate to={redirectPath} replace />;
}

function App() {
  return (
    <Router>
      <div>
        <Routes>
          <Route exact path="/" element={<Welcome />}></Route>
          <Route exact path="/forgot-password" element={<ForgotPassword />}></Route>
          <Route exact path="/reset-password" element={<ResetPassword />}></Route>
          <Route exact path="/create-account" element={<CreateAccount />}></Route>
          <Route exact path="/verify-email" element={<VerifyEmail />}></Route>
          <Route exact path="/welcome" element={<WelcomeForm />}></Route>
          <Route exact path="/player" element={<PlayerSigned />}></Route>
          
          {/* Developer Signup/Signin Routes */}
          <Route exact path="/developer/signin" element={<DeveloperWelcome />}></Route>
          <Route exact path="/developer/welcome" element={<DeveloperWelcomeForm />}></Route>
          
          {/* Playtester Routes */}
          <Route 
            exact 
            path="/dashboard" 
            element={
              <ProtectedRoute allowedRoles={['playtester']} requireProfile>
                <Dashboard />
              </ProtectedRoute>
            } 
          />

          <Route 
            exact 
            path="/settings" 
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            } 
          />

          <Route 
            exact 
            path="/rewards" 
            element={
              <ProtectedRoute>
                <Rewards />
              </ProtectedRoute>
            } 
          />

          <Route 
            exact 
            path="/validation-status" 
            element={
              <ProtectedRoute>
                <ValidationStatus />
              </ProtectedRoute>
            } 
          />
          
          {/* Developer Routes */}
          <Route
            path="/developer/*"
            element={
              <ProtectedRoute allowedRoles={['developer']} requireProfile>
                <DeveloperDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/studio/:developerId/*"
            element={
              <ProtectedRoute allowedRoles={['admin']}>
                <DeveloperDashboard />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route 
            exact 
            path="/admin" 
            element={
              <ProtectedRoute allowedRoles={['admin', 'developer']}>
                <AdminDashboard />
              </ProtectedRoute>
            } 
          />
          
          {/* Other routes */}
          <Route
            exact
            path="/upload-new-video"
            element={<UploadVideo />}
          ></Route>
          <Route
            exact
            path="/timeline-screen"
            element={<TimelineScreen />}
          ></Route>
          <Route exact path="/tencent" element={<Tencent />} />
          <Route exact path="/demo" element={<ImmerselyDemo />} />
          <Route exact path="/test-overwolf" element={<OverwolfAuthTest />} />
          
          {/* Default redirect */}
          <Route path="*" element={<DashboardRedirect />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
// <div className="App">
//   <header className="App-header">
//     <img src={logo} className="App-logo" alt="logo" />
//     <p>
//       Edit <code>src/App.js</code> and save to reload.
//     </p>
//     <a
//       className="App-link"
//       href="https://reactjs.org"
//       target="_blank"
//       rel="noopener noreferrer"
//     >
//       Learn React
//     </a>
//   </header>
// </div>


